import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SelectOption } from '@innowise-group/core';
import { SelectProps } from '@innowise-group/mui-kit';
import ExternalStateInfiniteScrollSelect, {
  InfiniteScrollSelectLoadParams,
} from './external-state-infinite-scroll-select.component';

export interface InfiniteScrollSelectPage {
  options: SelectProps['options'];
  totalPages: number;
}

export interface LocalStateInfiniteScrollSelectProps
  extends Omit<SelectProps, 'lastElementRef' | 'loading' | 'loadingAdditionalOptions' | 'options'> {
  initialOptions?: SelectProps['options'];
  selectedOption?: SelectOption;
  initialOptionsLoaded?: boolean;
  initialTotalPages?: number;
  initialPage?: number;
  debounceDelay?: number;
  loadOptions: (params: InfiniteScrollSelectLoadParams) => InfiniteScrollSelectPage | Promise<InfiniteScrollSelectPage>;
  onLoadError?: (error: unknown) => void;
}

const mergeUniqueOptions = (
  currentOptions: SelectProps['options'],
  loadedOptions: SelectProps['options'],
): SelectProps['options'] => {
  return [...currentOptions, ...loadedOptions].filter(
    (option, index, options) => options.findIndex(({ value }) => value === option.value) === index,
  );
};

const LocalStateInfiniteScrollSelect: React.FC<LocalStateInfiniteScrollSelectProps> = ({
  initialOptions = [],
  initialOptionsLoaded,
  initialTotalPages = 0,
  initialPage = 1,
  debounceDelay,
  loadOptions,
  onLoadError,
  ...props
}) => {
  const [options, setOptions] = useState<SelectProps['options']>(initialOptions);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const activeSearchValueRef = useRef('');
  const latestRequestIdRef = useRef(0);

  useEffect(() => {
    return () => {
      latestRequestIdRef.current += 1;
    };
  }, []);

  const loadLocalOptions = useCallback(
    async ({ page, searchValue }: InfiniteScrollSelectLoadParams) => {
      if (page === initialPage) {
        activeSearchValueRef.current = searchValue;
        setOptions([]);
        setTotalPages(0);
      } else if (activeSearchValueRef.current !== searchValue) {
        return;
      }

      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;
      setIsLoading(true);

      try {
        const loadedPage = await loadOptions({ page, searchValue });

        if (activeSearchValueRef.current !== searchValue || latestRequestIdRef.current !== requestId) return;

        setTotalPages(loadedPage.totalPages);
        setOptions((currentOptions) =>
          page === initialPage ? loadedPage.options : mergeUniqueOptions(currentOptions, loadedPage.options),
        );
      } catch (error) {
        if (activeSearchValueRef.current === searchValue && latestRequestIdRef.current === requestId) {
          onLoadError?.(error);
        }
      } finally {
        if (latestRequestIdRef.current === requestId) {
          setIsLoading(false);
        }
      }
    },
    [initialPage, loadOptions, onLoadError],
  );

  return (
    <ExternalStateInfiniteScrollSelect
      {...props}
      options={options}
      isLoading={isLoading}
      totalPages={totalPages}
      initialOptionsLoaded={initialOptionsLoaded ?? initialOptions.length > 0}
      initialPage={initialPage}
      debounceDelay={debounceDelay}
      loadOptions={loadLocalOptions}
    />
  );
};

export default LocalStateInfiniteScrollSelect;
